// src/app/core/guards/auth.guard.ts

import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private authService = inject(AuthService);
  private router = inject(Router);

  // Opcional (consistência com Firestore)
  private firestore = inject(Firestore);

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {

    /* =====================================================
     * 0) BOOTSTRAP SAFE
     * -----------------------------------------------------
     * Evita tela branca silenciosa em cold start:
     * se route.routeConfig vier null, não bloqueia.
     * ===================================================== */
    if (!route.routeConfig) {
      return true;
    }

    /* =====================================================
     * 1) BYPASS GLOBAL DURANTE VERIFICAÇÃO DE E-MAIL
     * -----------------------------------------------------
     * Enquanto o token estiver sendo processado:
     * - nenhum guard deve redirecionar
     * - evita loop e race conditions
     * ===================================================== */
    if (this.authService.isProcessingEmailVerification()) {
      console.log('AuthGuard: bypass ativo (processingEmailVerification)');
      return true;
    }

    /* =====================================================
     * 2) AGUARDA AUTH RESOLVER (ANTI-F5 / COLD START)
     * ===================================================== */
    const authUser = await this.authService.waitForAuthReady();

    /* =====================================================
     * 3) NÃO AUTENTICADO → REDIRECIONA PARA SIGNIN
     * ===================================================== */
    if (!authUser) {
      return this.router.createUrlTree(['/registration/signin'], {
        queryParams: { redirect: state.url }
      });
    }

    /* =====================================================
     * 4) BLOCO OPCIONAL: CONSISTÊNCIA (EMULATOR / DEV)
     * -----------------------------------------------------
     * Caso comum no Emulator:
     * - Auth possui sessão/persistência local
     * - mas o usuário foi apagado do Firestore manualmente
     * Resultado: serviços quebram (ex.: ProgressTrackerService)
     *
     * Estratégia mínima:
     * - se não existir doc users/{uid}, trata como "sessão inválida"
     * - redireciona para signin (com replaceUrl recomendado no componente)
     *
     * ⚠️ Em produção, você pode desativar isso ou tornar "soft"
     * (ex.: tentar recriar doc base).
     * ===================================================== */
    const ENABLE_FIRESTORE_USERDOC_CHECK = true;

    if (ENABLE_FIRESTORE_USERDOC_CHECK) {
      try {
        const userRef = doc(this.firestore, `users/${authUser.uid}`);
        const snap = await getDoc(userRef);

        if (!snap.exists()) {
          console.warn(
            'AuthGuard: Auth ok, mas users/{uid} não existe no Firestore. Redirecionando para signin.',
            { uid: authUser.uid }
          );

          // Importante: não chame signOut aqui dentro do guard para evitar loops.
          // Apenas roteie para uma rota pública e deixe a tela de login/fluxo resolver.
          return this.router.createUrlTree(['/registration/signin'], {
            queryParams: {
              reason: 'missing_user_doc',
              redirect: state.url
            }
          });
        }

      } catch (err) {
        // Se o check falhar (emulator offline, permissão, etc),
        // não bloqueie agressivamente: apenas deixa passar.
        console.warn('AuthGuard: Falha no check de consistência Firestore. Liberando acesso.', err);
        return true;
      }
    }

    return true;
  }
}
